import { useQuery } from '@tanstack/react-query';
import { educationKeys, type EducationSessionQuery } from '@platform/billing';
import { educationApi } from './education.api';

/** 공개 과정 목록(ACTIVE 만). family 로 XCOST / XPIXELS 를 가른다. */
export function useEducationCourses(family?: string) {
  return useQuery({
    queryKey: educationKeys.courses(family),
    queryFn: () => educationApi.courses(family),
  });
}

export function useEducationCourse(id: string | undefined) {
  return useQuery({
    queryKey: educationKeys.course(id ?? ''),
    queryFn: () => educationApi.course(id as string),
    enabled: !!id,
  });
}

/**
 * 회차 목록. 잔여석이 실려 오므로 stale 하게 두면 마감된 회차를 담게 된다 —
 * 접수 성공/실패 때 무효화하는 것으로는 부족해 창 포커스마다 다시 읽는다.
 */
export function useEducationSessions(query: EducationSessionQuery = {}) {
  return useQuery({
    queryKey: educationKeys.sessions(JSON.stringify(query)),
    queryFn: () => educationApi.sessions(query),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/**
 * 기업 접수의 구성원 선택지.
 *
 * `sessionIds` 를 넘기면 각자 이미 접수된 회차가 함께 온다. 이 값은 남이 접수하면
 * 바뀌므로 캐시를 오래 들고 있으면 안 된다 — 화면에서 고를 수 있었던 사람이 제출
 * 단계에서 거부된다. 회차를 안 물어보는 호출만 길게 캐시한다.
 */
export function useEducationMemberOptions(
  enabled: boolean,
  sessionIds: string[] = [],
) {
  return useQuery({
    queryKey: educationKeys.memberOptions(sessionIds),
    queryFn: () => educationApi.memberOptions(sessionIds),
    enabled,
    staleTime: sessionIds.length ? 0 : 5 * 60 * 1000,
  });
}

export function useMyEducationRegistrations(page = 1) {
  return useQuery({
    queryKey: educationKeys.myRegistrations(page),
    queryFn: () => educationApi.myRegistrations(page),
  });
}

export function useMyEducationRegistration(id: string | undefined) {
  return useQuery({
    queryKey: educationKeys.myRegistration(id ?? ''),
    queryFn: () => educationApi.myRegistration(id as string),
    enabled: !!id,
  });
}
